/**
 * Browser history — the "Recently used" half of the launcher.
 *
 * Ported from t3code's `apps/web/src/browserHistoryStore.ts`, trimmed to what
 * Forge can actually populate. Their store also resolves favicons and title
 * aliases through a second store fed by Electron's `page-favicon-updated`;
 * Forge's `BrowserTabSnapshot` carries a title but no favicon, so this keeps
 * titles and leaves favicons out rather than shipping an empty icon column.
 *
 * Scoped by project, not by thread: a thread is one unit of work, but the
 * localhost URL you were just looking at is a property of the codebase, and
 * scoping per thread would hand every new thread an empty launcher.
 *
 * @module browserHistoryStore
 */
import * as Schema from "effect/Schema";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { createMemoryStorage } from "~/lib/storage";

import { isDevTunnelPathUrl } from "./browserLauncherLogic";

const BROWSER_HISTORY_STORAGE_KEY = "forge:browser-history:v1";
const BROWSER_HISTORY_STORAGE_VERSION = 1;

/** Per scope. Above this the oldest visit is dropped. */
export const BROWSER_HISTORY_MAX_ENTRIES_PER_SCOPE = 50;
/** Total scopes retained, so an old project cannot grow the payload forever. */
export const BROWSER_HISTORY_MAX_SCOPES = 20;
export const BROWSER_HISTORY_MAX_URL_LENGTH = 2048;
export const BROWSER_HISTORY_MAX_TITLE_LENGTH = 512;

/** `new Date(ms)` throws beyond this, so a bad row must never reach a Date. */
const MAX_VALID_DATE_MS = 8_640_000_000_000_000;

export interface BrowserHistoryEntry {
  readonly url: string;
  readonly lastVisitedAt: number;
  readonly title?: string | undefined;
}

const PersistedBrowserHistoryEntry = Schema.Struct({
  url: Schema.String,
  lastVisitedAt: Schema.Number,
  title: Schema.optional(Schema.String),
});

const PersistedBrowserHistoryState = Schema.Struct({
  entriesByScope: Schema.Record(Schema.String, Schema.Array(PersistedBrowserHistoryEntry)),
});

const decodePersistedState = Schema.decodeUnknownOption(PersistedBrowserHistoryState);

export function isValidHistoryTimestamp(value: unknown): value is number {
  return (
    typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= MAX_VALID_DATE_MS
  );
}

/**
 * The storable form of a URL, or null when it should not be stored at all.
 *
 * Three rejections, each for its own reason:
 *
 * - **Credentials** are stripped, because a URL typed with `user:pass@` would
 *   otherwise be persisted to disk in cleartext and rendered back on screen.
 * - **Non-http schemes** are dropped. They are not places a user meant to visit,
 *   and the panel parks on `about:blank` between navigations, so storing it
 *   would fill the launcher with blank rows.
 * - **Dev-tunnel URLs** are dropped, because they carry a signed
 *   `forgeTunnelToken`. Forge's rule is that the user-facing form of a tunnelled
 *   preview is its loopback URL (`browserDisplayUrl`), and a token persisted to
 *   localStorage would outlive the tunnel that minted it. Callers record the
 *   display URL; this is the backstop that holds even if one forgets.
 */
export function normalizeHistoryUrl(raw: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(raw.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return null;
  }
  if (isDevTunnelPathUrl(parsed.href)) {
    return null;
  }
  parsed.username = "";
  parsed.password = "";
  return parsed.href.length > BROWSER_HISTORY_MAX_URL_LENGTH ? null : parsed.href;
}

function normalizeHistoryTitle(title: string | null | undefined): string | undefined {
  const trimmed = title?.trim() ?? "";
  if (trimmed === "") {
    return undefined;
  }
  return trimmed.length > BROWSER_HISTORY_MAX_TITLE_LENGTH
    ? trimmed.slice(0, BROWSER_HISTORY_MAX_TITLE_LENGTH)
    : trimmed;
}

/**
 * Newest-first entries with `url` moved to the front.
 *
 * A revisit updates the existing row rather than adding one, and keeps the old
 * title when the new visit has none — a navigation that reports no title yet
 * should not blank a title the user already recognises.
 */
export function upsertHistoryEntry(
  entries: ReadonlyArray<BrowserHistoryEntry>,
  input: { readonly url: string; readonly title?: string | undefined; readonly visitedAt: number },
): ReadonlyArray<BrowserHistoryEntry> {
  const previous = entries.find((entry) => entry.url === input.url);
  const title = normalizeHistoryTitle(input.title) ?? previous?.title;
  const next: BrowserHistoryEntry = {
    url: input.url,
    lastVisitedAt: input.visitedAt,
    ...(title === undefined ? {} : { title }),
  };
  return [next, ...entries.filter((entry) => entry.url !== input.url)].slice(
    0,
    BROWSER_HISTORY_MAX_ENTRIES_PER_SCOPE,
  );
}

/**
 * Drops the least-recently-touched scopes past the cap.
 *
 * Recency of a scope is its newest entry, so the project you last previewed
 * survives even when another project has more rows.
 */
export function evictExcessScopes(
  entriesByScope: Readonly<Record<string, ReadonlyArray<BrowserHistoryEntry>>>,
): Record<string, ReadonlyArray<BrowserHistoryEntry>> {
  const scopes = Object.keys(entriesByScope);
  if (scopes.length <= BROWSER_HISTORY_MAX_SCOPES) {
    return { ...entriesByScope };
  }
  const ranked = scopes
    .map((scope) => ({
      scope,
      newest: Math.max(0, ...(entriesByScope[scope] ?? []).map((entry) => entry.lastVisitedAt)),
    }))
    .toSorted((left, right) => right.newest - left.newest)
    .slice(0, BROWSER_HISTORY_MAX_SCOPES);
  const kept: Record<string, ReadonlyArray<BrowserHistoryEntry>> = {};
  for (const { scope } of ranked) {
    kept[scope] = entriesByScope[scope] ?? [];
  }
  return kept;
}

/**
 * Keeps only rows that will render. A persisted row with an unparseable URL or
 * a timestamp `new Date()` would throw on is dropped on read, so one bad write
 * cannot break the launcher for good.
 */
export function sanitizePersistedEntries(
  entries: ReadonlyArray<{
    readonly url: string;
    readonly lastVisitedAt: number;
    readonly title?: string | undefined;
  }>,
): ReadonlyArray<BrowserHistoryEntry> {
  const seen = new Set<string>();
  const sanitized: BrowserHistoryEntry[] = [];
  for (const entry of entries) {
    const url = normalizeHistoryUrl(entry.url);
    if (url === null || seen.has(url) || !isValidHistoryTimestamp(entry.lastVisitedAt)) {
      continue;
    }
    seen.add(url);
    const title = normalizeHistoryTitle(entry.title);
    sanitized.push({
      url,
      lastVisitedAt: entry.lastVisitedAt,
      ...(title === undefined ? {} : { title }),
    });
  }
  return sanitized
    .toSorted((left, right) => right.lastVisitedAt - left.lastVisitedAt)
    .slice(0, BROWSER_HISTORY_MAX_ENTRIES_PER_SCOPE);
}

export function migratePersistedBrowserHistoryState(persistedState: unknown): {
  readonly entriesByScope: Record<string, ReadonlyArray<BrowserHistoryEntry>>;
} {
  const decoded = decodePersistedState(persistedState);
  if (decoded._tag === "None") {
    return { entriesByScope: {} };
  }
  const entriesByScope: Record<string, ReadonlyArray<BrowserHistoryEntry>> = {};
  for (const [scope, entries] of Object.entries(decoded.value.entriesByScope)) {
    const sanitized = sanitizePersistedEntries(entries);
    if (sanitized.length > 0) {
      entriesByScope[scope] = sanitized;
    }
  }
  return { entriesByScope: evictExcessScopes(entriesByScope) };
}

interface BrowserHistoryState {
  readonly entriesByScope: Record<string, ReadonlyArray<BrowserHistoryEntry>>;
  readonly recordVisit: (input: {
    readonly scope: string;
    readonly url: string;
    readonly title?: string | undefined;
    readonly visitedAt: number;
  }) => void;
  readonly removeEntry: (input: { readonly scope: string; readonly url: string }) => void;
}

const historyStorage = typeof localStorage === "undefined" ? createMemoryStorage() : localStorage;

export const useBrowserHistoryStore = create<BrowserHistoryState>()(
  persist(
    (set) => ({
      entriesByScope: {},
      recordVisit: (input) => {
        const url = normalizeHistoryUrl(input.url);
        if (url === null || !isValidHistoryTimestamp(input.visitedAt)) {
          return;
        }
        set((state) => {
          const current = state.entriesByScope[input.scope] ?? [];
          const updated = upsertHistoryEntry(current, {
            url,
            title: input.title,
            visitedAt: input.visitedAt,
          });
          return {
            entriesByScope: evictExcessScopes({
              ...state.entriesByScope,
              [input.scope]: updated,
            }),
          };
        });
      },
      removeEntry: (input) => {
        const url = normalizeHistoryUrl(input.url);
        if (url === null) {
          return;
        }
        set((state) => {
          const current = state.entriesByScope[input.scope];
          if (current === undefined) {
            return state;
          }
          const remaining = current.filter((entry) => entry.url !== url);
          const entriesByScope = { ...state.entriesByScope };
          if (remaining.length === 0) {
            delete entriesByScope[input.scope];
          } else {
            entriesByScope[input.scope] = remaining;
          }
          return { entriesByScope };
        });
      },
    }),
    {
      name: BROWSER_HISTORY_STORAGE_KEY,
      version: BROWSER_HISTORY_STORAGE_VERSION,
      storage: createJSONStorage(() => historyStorage),
      partialize: (state) => ({ entriesByScope: state.entriesByScope }),
      migrate: (persistedState) => migratePersistedBrowserHistoryState(persistedState),
    },
  ),
);

/**
 * The launcher's history scope.
 *
 * Project when there is one. A thread with no project (a scratch thread) still
 * gets a stable bucket rather than sharing one global list with every other
 * projectless thread, since those threads have nothing to do with each other.
 */
export function browserHistoryScopeKey(input: {
  readonly environmentId: string;
  readonly projectId?: string | null | undefined;
  readonly threadId: string;
}): string {
  return input.projectId
    ? `project:${input.environmentId}:${input.projectId}`
    : `thread:${input.environmentId}:${input.threadId}`;
}

/**
 * A scope's entries, or a stable empty array.
 *
 * The shared constant matters: returning a fresh `[]` would give every render a
 * new reference and re-render the launcher on unrelated store writes.
 */
const NO_ENTRIES: ReadonlyArray<BrowserHistoryEntry> = Object.freeze([]);

export function selectBrowserHistoryEntries(
  state: { readonly entriesByScope: Record<string, ReadonlyArray<BrowserHistoryEntry>> },
  scope: string,
): ReadonlyArray<BrowserHistoryEntry> {
  return state.entriesByScope[scope] ?? NO_ENTRIES;
}
